/**
 * Vanilla parallax and scroll-reveal for the public marketing vitrine.
 * No external animation libraries. Cleans up listeners on destroy.
 */

const LERP = 0.08;
const MOUSE_KPI_FACTOR = 0.3;
const TILT_MAX_X = 8;
const TILT_MAX_Y = 10;
const REVEAL_STAGGER_MS = 80;

type ScrollParallaxNode = {
  el: HTMLElement;
  speed: number;
  currentY: number;
  targetY: number;
};

type MouseParallaxNode = {
  el: HTMLElement;
  kind: "blob" | "kpi" | "tilt";
  currentX: number;
  currentY: number;
  targetX: number;
  targetY: number;
};

function prefersReducedMotion(): boolean {
  return typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function isFinePointer(): boolean {
  return typeof window !== "undefined" && window.matchMedia("(hover: hover) and (pointer: fine)").matches;
}

function lerp(current: number, target: number, factor: number): number {
  return current + (target - current) * factor;
}

export const Parallax = {
  root: null as HTMLElement | null,
  hero: null as HTMLElement | null,
  scrollNodes: [] as ScrollParallaxNode[],
  mouseNodes: [] as MouseParallaxNode[],
  mouse: { x: 0, y: 0, lerpX: 0, lerpY: 0 },
  scrollY: 0,
  rafId: 0 as number | 0,
  enabled: false,
  mouseEnabled: false,

  init(root: HTMLElement) {
    if (prefersReducedMotion()) return;

    this.destroy();
    this.root = root;
    this.hero = root.querySelector<HTMLElement>("[data-parallax-hero]");
    this.enabled = true;
    this.mouseEnabled = isFinePointer();

    root.querySelectorAll<HTMLElement>("[data-speed]").forEach((el) => {
      const speed = Number.parseFloat(el.dataset.speed ?? "0.2");
      this.scrollNodes.push({
        el,
        speed: Number.isFinite(speed) ? speed : 0.2,
        currentY: 0,
        targetY: 0,
      });
    });

    if (this.hero && this.mouseEnabled) {
      this.hero.querySelectorAll<HTMLElement>("[data-parallax-mouse]").forEach((el) => {
        this.mouseNodes.push({
          el,
          kind: "blob",
          currentX: 0,
          currentY: 0,
          targetX: 0,
          targetY: 0,
        });
      });

      this.hero.querySelectorAll<HTMLElement>("[data-parallax-kpi]").forEach((el) => {
        this.mouseNodes.push({
          el,
          kind: "kpi",
          currentX: 0,
          currentY: 0,
          targetX: 0,
          targetY: 0,
        });
      });

      this.hero.querySelectorAll<HTMLElement>("[data-parallax-tilt]").forEach((el) => {
        this.mouseNodes.push({
          el,
          kind: "tilt",
          currentX: 0,
          currentY: 0,
          targetX: 0,
          targetY: 0,
        });
      });
    }

    this.bindEvents();
    this.handleScroll();
    this.tick();
  },

  bindEvents() {
    if (!this.enabled) return;

    window.addEventListener("scroll", this.onScroll, { passive: true });
    window.addEventListener("resize", this.onScroll, { passive: true });

    if (this.hero && this.mouseEnabled) {
      this.hero.addEventListener("mousemove", this.onMouseMove);
      this.hero.addEventListener("mouseleave", this.onMouseLeave);
    }
  },

  onScroll: () => {
    Parallax.handleScroll();
  },

  onMouseMove: (event: MouseEvent) => {
    Parallax.handleMouse(event);
  },

  onMouseLeave: () => {
    Parallax.mouse.x = 0;
    Parallax.mouse.y = 0;
  },

  handleScroll() {
    this.scrollY = window.scrollY;
    for (const node of this.scrollNodes) {
      node.targetY = this.scrollY * node.speed;
    }
  },

  handleMouse(event: MouseEvent) {
    if (!this.hero) return;

    const rect = this.hero.getBoundingClientRect();
    const relX = (event.clientX - rect.left) / rect.width - 0.5;
    const relY = (event.clientY - rect.top) / rect.height - 0.5;

    this.mouse.x = Math.max(-0.5, Math.min(0.5, relX));
    this.mouse.y = Math.max(-0.5, Math.min(0.5, relY));

    for (const node of this.mouseNodes) {
      const driftX = -this.mouse.x * 24;
      const driftY = -this.mouse.y * 18;

      if (node.kind === "blob") {
        node.targetX = driftX;
        node.targetY = driftY;
      } else if (node.kind === "kpi") {
        node.targetX = driftX * MOUSE_KPI_FACTOR;
        node.targetY = driftY * MOUSE_KPI_FACTOR;
      } else {
        node.targetX = this.mouse.x * TILT_MAX_Y;
        node.targetY = -this.mouse.y * TILT_MAX_X;
      }
    }
  },

  tick() {
    if (!this.enabled) return;

    for (const node of this.scrollNodes) {
      node.currentY = lerp(node.currentY, node.targetY, LERP);
      node.el.style.transform = `translate3d(0, ${node.currentY.toFixed(2)}px, 0)`;
    }

    if (this.mouseEnabled) {
      this.mouse.lerpX = lerp(this.mouse.lerpX, this.mouse.x, LERP);
      this.mouse.lerpY = lerp(this.mouse.lerpY, this.mouse.y, LERP);

      for (const node of this.mouseNodes) {
        node.currentX = lerp(node.currentX, node.targetX, LERP);
        node.currentY = lerp(node.currentY, node.targetY, LERP);

        if (node.kind === "tilt") {
          node.el.style.transform = `perspective(1000px) rotateX(${node.currentY.toFixed(2)}deg) rotateY(${node.currentX.toFixed(2)}deg)`;
        } else {
          node.el.style.transform = `translate3d(${node.currentX.toFixed(2)}px, ${node.currentY.toFixed(2)}px, 0)`;
        }
      }
    }

    this.rafId = window.requestAnimationFrame(() => this.tick());
  },

  destroy() {
    window.removeEventListener("scroll", this.onScroll);
    window.removeEventListener("resize", this.onScroll);

    if (this.hero) {
      this.hero.removeEventListener("mousemove", this.onMouseMove);
      this.hero.removeEventListener("mouseleave", this.onMouseLeave);
    }

    if (this.rafId) {
      window.cancelAnimationFrame(this.rafId);
      this.rafId = 0;
    }

    for (const node of this.scrollNodes) {
      node.el.style.transform = "";
    }
    for (const node of this.mouseNodes) {
      node.el.style.transform = "";
    }

    this.root = null;
    this.hero = null;
    this.scrollNodes = [];
    this.mouseNodes = [];
    this.enabled = false;
    this.mouseEnabled = false;
    this.mouse = { x: 0, y: 0, lerpX: 0, lerpY: 0 };
  },
};

export const ScrollReveal = {
  observer: null as IntersectionObserver | null,
  root: null as HTMLElement | null,

  init(root: HTMLElement) {
    this.destroy();
    this.root = root;

    if (prefersReducedMotion()) {
      root.querySelectorAll<HTMLElement>("[data-reveal-item]").forEach((item) => {
        item.classList.add("is-revealed");
      });
      return;
    }

    const sections = root.querySelectorAll<HTMLElement>("[data-reveal-section]");
    if (sections.length === 0) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          const section = entry.target as HTMLElement;
          const items = section.querySelectorAll<HTMLElement>("[data-reveal-item]");
          items.forEach((item, index) => {
            item.style.transitionDelay = `${index * REVEAL_STAGGER_MS}ms`;
            item.classList.add("is-revealed");
          });

          this.observer?.unobserve(section);
        });
      },
      { threshold: 0.15 },
    );

    sections.forEach((section) => this.observer?.observe(section));
  },

  destroy() {
    this.observer?.disconnect();
    this.observer = null;
    this.root = null;
  },
};

/** Bootstraps vitrine effects; returns cleanup for React useEffect. */
export function initMarketingEffects(root: HTMLElement): () => void {
  Parallax.init(root);
  ScrollReveal.init(root);

  return () => {
    Parallax.destroy();
    ScrollReveal.destroy();
  };
}
